const API_BASE = import.meta.env.VITE_API_BASE ?? '';

function getToken(): string | null {
  return localStorage.getItem('eric_ai_token');
}

export function setToken(token: string): void {
  localStorage.setItem('eric_ai_token', token);
}

export function clearToken(): void {
  localStorage.removeItem('eric_ai_token');
}

type RequestOptions = Omit<RequestInit, 'body'> & { body?: unknown };

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> ?? {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
    cache: 'no-store',
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText })) as { error?: string };
    throw new Error(err.error ?? `HTTP ${res.status}`);
  }

  return res.json() as Promise<T>;
}

export const api = {
  // Auth
  login: (username: string, password: string) =>
    request<{ token: string; user: any }>('/api/auth/login', { method: 'POST', body: { username, password } }),

  register: (username: string, password: string, email?: string) =>
    request<{ token: string; user: any }>('/api/auth/register', { method: 'POST', body: { username, password, email } }),

  me: () => request<any>('/api/auth/me'),
  logout: () => request<any>('/api/auth/logout', { method: 'POST' }),
  changePassword: (old_password: string, new_password: string) =>
    request<any>('/api/auth/change-password', { method: 'POST', body: { old_password, new_password } }),

  // Nodes
  getPrompt: (role: string) => request<any>(`/api/nodes/prompts/${role}`),
  updatePrompt: (role: string, prompt: string) =>
    request<any>(`/api/nodes/prompts/${role}`, { method: 'PUT', body: { prompt } }),
  resetPrompt: (role: string) =>
    request<any>(`/api/nodes/prompts/${role}`, { method: 'DELETE' }),

  // Provider config
  getProviderConfig: () => request<any>('/api/admin/provider-config'),
  updateNodeChain: (node_role: string, chain: any[], default_switch?: string, default_registry?: string) =>
    request<any>('/api/admin/provider-config/node', { method: 'POST', body: { node_role, chain, default_switch, default_registry } }),
  updateGlobalApiKeys: (api_keys: Record<string, string>) =>
    request<any>('/api/admin/provider-config/keys', { method: 'POST', body: { api_keys } }),
  updateSwitchPromptSettings: (settings: { enable_checkpoints: boolean; enable_lenses: boolean }) =>
    request<any>('/api/admin/provider-config/switch-prompt', { method: 'POST', body: settings }),
  testProvider: (data: any) =>
    request<any>('/api/admin/provider-config/test', { method: 'POST', body: data }),
  getModelRegistry: () => request<any>('/api/admin/provider-config/models'),

  // Projects
  listProjects: () => request<any>('/api/projects'),
  getProjectTree: (id: string, prefix = '') =>
    request<any>(`/api/projects/${id}/tree?prefix=${encodeURIComponent(prefix)}`),
  getFileContent: (id: string, path: string) =>
    request<any>(`/api/projects/${id}/files/${path}`),
  getProjectStatus: (id: string) =>
    request<any>(`/api/projects/${id}/status`),

  // API Keys
  listApiKeys: () => request<any>('/api/api-keys'),
  createApiKey: (name: string) =>
    request<any>('/api/api-keys', { method: 'POST', body: { name } }),
  revokeApiKey: (id: string) =>
    request<any>(`/api/api-keys/${id}`, { method: 'DELETE' }),

  // Stats
  getStats: () => request<any>('/api/stats'),

  // User Profile
  getProfile: () => request<any>('/api/user/me'),

  // Admin Users
  adminGetUsers: () => request<any>('/api/admin/users'),
  adminCreateUser: (user: any) => request<any>('/api/admin/users', { method: 'POST', body: user }),
  adminUpdateUser: (id: string, user: any) => request<any>(`/api/admin/users/${id}`, { method: 'PUT', body: user }),
  adminDeleteUser: (id: string) => request<any>(`/api/admin/users/${id}`, { method: 'DELETE' }),
  adminAssignPlan: (user_id: string, plan_id: string) => request<any>('/api/admin/users/plan', { method: 'POST', body: { user_id, plan_id } }),

  // Admin Plans
  adminGetPlans: () => request<any>('/api/admin/plans'),
  adminCreatePlan: (plan: any) => request<any>('/api/admin/plans', { method: 'POST', body: plan }),
  adminDeletePlan: (id: string) => request<any>(`/api/admin/plans/${id}`, { method: 'DELETE' }),

  // Prompt Forge (RLHF)
  adminConvertPromptForge: (domain_id: string, feedback: string) => 
    request<any>('/api/admin/prompt-forge', { method: 'POST', body: { domain_id, feedback } }),
    
  // Feedbacks
  submitFeedback: (session_id: string, score: number, feedback_text?: string) =>
    request<any>('/api/chat/feedback', { method: 'POST', body: { session_id, score, feedback_text } }),
  adminGetFeedbacks: () => request<any>('/api/admin/feedbacks'),

  // Billing & Stripe
  createCheckoutSession: (priceId: string, type: 'topup' | 'subscription') =>
    request<{ checkout_url: string }>('/api/billing/create-checkout-session', { method: 'POST', body: { priceId, type } }),

  // Chat Sessions (R2)
  getSessions: () => request<{ sessions: any[] }>('/v1/chat/sessions'),
  getSessionDetails: (id: string) => request<any>(`/v1/chat/sessions/${id}`),
  renameSession: (id: string, title: string) => request<any>(`/v1/chat/sessions/${id}`, { method: 'PUT', body: { title } }),
  deleteSession: (id: string) => request<any>(`/v1/chat/sessions/${id}`, { method: 'DELETE' }),

  // File Upload
  uploadFile: (file: File, sessionId: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('sessionId', sessionId);
    
    // We cannot use standard JSON request wrapper for FormData
    const token = localStorage.getItem('token');
    return fetch('/v1/chat/upload', {
      method: 'POST',
      headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      body: formData,
    }).then(res => res.json());
  },

  // ── Swarm V2 Admin APIs ────────────────────────────────────────
  v2GetVersion: () => request<{ version: string }>('/api/admin/v2/version'),
  v2SetVersion: (version: 'v1' | 'v2') =>
    request<{ ok: boolean; version: string }>('/api/admin/v2/version', { method: 'PUT', body: { version } }),

  // Switch Prompts
  v2ListSwitchPrompts: () => request<{ items: { key: string; content: string }[] }>('/api/admin/v2/switch-prompts'),
  v2GetSwitchPrompt: (key: string) => request<{ key: string; content: string }>(`/api/admin/v2/switch-prompts/${key}`),
  v2SaveSwitchPrompt: (key: string, content: string) =>
    request<{ ok: boolean }>(`/api/admin/v2/switch-prompts/${key}`, { method: 'PUT', body: { content } }),
  v2DeleteSwitchPrompt: (key: string) =>
    request<{ ok: boolean }>(`/api/admin/v2/switch-prompts/${key}`, { method: 'DELETE' }),

  // Registry Prompts
  v2ListRegistryPrompts: () => request<{ items: { key: string; content: string }[] }>('/api/admin/v2/registry-prompts'),
  v2GetRegistryPrompt: (key: string) => request<{ key: string; content: string }>(`/api/admin/v2/registry-prompts/${key}`),
  v2SaveRegistryPrompt: (key: string, content: string) =>
    request<{ ok: boolean }>(`/api/admin/v2/registry-prompts/${key}`, { method: 'PUT', body: { content } }),
  v2DeleteRegistryPrompt: (key: string) =>
    request<{ ok: boolean }>(`/api/admin/v2/registry-prompts/${key}`, { method: 'DELETE' }),

  // Seed defaults
  v2SeedDefaults: () =>
    request<{ ok: boolean; seeded: number; switch_prompts: string[]; registry_prompts: string[] }>(
      '/api/admin/v2/seed-defaults', { method: 'POST' }
    ),

  // Sync V1 → V2 Registry (đồng bộ sys_prompt_v2:* vào v2_registry:*)
  v2SyncV1: () =>
    request<{ ok: boolean; synced: number; roles: string[] }>(
      '/api/admin/v2/sync-v1', { method: 'POST' }
    ),
};
